// Country constants for search filter pickers.
//
// The Crustdata API has an annoying format inconsistency that the
// portal has to bridge:
//   - PersonDB ``current_employers.company_headquarters_country`` (and
//     past/all_employers mirrors) returns FULL NAMES like
//     "United States of America", "France", "Germany".
//   - CompanyDB ``hq_country`` returns ISO-3 codes like
//     "USA", "GBR", "FRA".
//
// To keep both filter pickers driven from one source of truth, every
// row carries both forms plus a friendly display label. The picker
// emits whichever form matches the tab it's mounted on.

export interface CountryRow {
  /** ISO-3166-1 alpha-3 — wire value for CompanyDB filters. */
  iso3: string;
  /** Full name as Crustdata returns on PersonDB employer fields. */
  full: string;
  /** UI label — short, user-friendly. */
  display: string;
}

// Curated list of the countries that actually appear in Crustdata
// data with meaningful volume. Probe-verified via /screener/persondb
// /autocomplete on current_employers.company_headquarters_country.
//
// Adding more is safe: the picker falls through to a free-text input
// if the user types something not in the list, so partial coverage
// just hurts discoverability not correctness.
export const COUNTRIES: CountryRow[] = [
  { iso3: 'USA', full: 'United States of America', display: 'United States' },
  {
    iso3: 'GBR',
    full: 'United Kingdom of Great Britain and Northern Ireland',
    display: 'United Kingdom',
  },
  { iso3: 'CAN', full: 'Canada', display: 'Canada' },
  { iso3: 'DEU', full: 'Germany', display: 'Germany' },
  { iso3: 'FRA', full: 'France', display: 'France' },
  { iso3: 'AUS', full: 'Australia', display: 'Australia' },
  { iso3: 'NLD', full: 'Netherlands', display: 'Netherlands' },
  { iso3: 'IND', full: 'India', display: 'India' },
  { iso3: 'SGP', full: 'Singapore', display: 'Singapore' },
  { iso3: 'BRA', full: 'Brazil', display: 'Brazil' },
  { iso3: 'MEX', full: 'Mexico', display: 'Mexico' },
  { iso3: 'ESP', full: 'Spain', display: 'Spain' },
  { iso3: 'ITA', full: 'Italy', display: 'Italy' },
  { iso3: 'SWE', full: 'Sweden', display: 'Sweden' },
  { iso3: 'CHE', full: 'Switzerland', display: 'Switzerland' },
  { iso3: 'JPN', full: 'Japan', display: 'Japan' },
  { iso3: 'KOR', full: 'South Korea', display: 'South Korea' },
  { iso3: 'CHN', full: 'China', display: 'China' },
  { iso3: 'ISR', full: 'Israel', display: 'Israel' },
  {
    iso3: 'ARE',
    full: 'United Arab Emirates',
    display: 'United Arab Emirates',
  },
  { iso3: 'IRL', full: 'Ireland', display: 'Ireland' },
  { iso3: 'BEL', full: 'Belgium', display: 'Belgium' },
  { iso3: 'AUT', full: 'Austria', display: 'Austria' },
  { iso3: 'POL', full: 'Poland', display: 'Poland' },
  { iso3: 'DNK', full: 'Denmark', display: 'Denmark' },
  { iso3: 'NOR', full: 'Norway', display: 'Norway' },
  { iso3: 'FIN', full: 'Finland', display: 'Finland' },
  { iso3: 'PRT', full: 'Portugal', display: 'Portugal' },
  { iso3: 'NZL', full: 'New Zealand', display: 'New Zealand' },
  { iso3: 'ZAF', full: 'South Africa', display: 'South Africa' },
  { iso3: 'ARG', full: 'Argentina', display: 'Argentina' },
  { iso3: 'CHL', full: 'Chile', display: 'Chile' },
  { iso3: 'COL', full: 'Colombia', display: 'Colombia' },
  { iso3: 'IDN', full: 'Indonesia', display: 'Indonesia' },
  { iso3: 'MYS', full: 'Malaysia', display: 'Malaysia' },
  { iso3: 'PHL', full: 'Philippines', display: 'Philippines' },
  { iso3: 'THA', full: 'Thailand', display: 'Thailand' },
  { iso3: 'VNM', full: 'Vietnam', display: 'Vietnam' },
  { iso3: 'TWN', full: 'Taiwan', display: 'Taiwan' },
  { iso3: 'HKG', full: 'Hong Kong', display: 'Hong Kong' },
  { iso3: 'TUR', full: 'Turkey', display: 'Turkey' },
  { iso3: 'SAU', full: 'Saudi Arabia', display: 'Saudi Arabia' },
  { iso3: 'EGY', full: 'Egypt', display: 'Egypt' },
  { iso3: 'NGA', full: 'Nigeria', display: 'Nigeria' },
  { iso3: 'KEN', full: 'Kenya', display: 'Kenya' },
  { iso3: 'CZE', full: 'Czech Republic', display: 'Czech Republic' },
  { iso3: 'HUN', full: 'Hungary', display: 'Hungary' },
  { iso3: 'ROU', full: 'Romania', display: 'Romania' },
  { iso3: 'UKR', full: 'Ukraine', display: 'Ukraine' },
  { iso3: 'GRC', full: 'Greece', display: 'Greece' },
];
